import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import {
  GooglePlaceAddress,
  GooglePlaceOptions,
  toPlaceAutocompleteOptions,
} from './google-place-options';
import { loadGoogleMapsPlacesLibrary } from '@app/core/google-maps-bootstrap';

@Component({
  standalone: true,
  selector: 'app-google-place-autocomplete',
  templateUrl: './google-place-autocomplete.component.html',
  styleUrls: ['./google-place-autocomplete.component.css'],
  exportAs: 'googlePlaceAutocomplete',
})
export class GooglePlaceAutocompleteComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() options: GooglePlaceOptions = {};
  @Input() placeholder = '';
  /** Passed to the lazy-load fallback input for autofill and label association. */
  @Input() inputId = 'listing-search';
  @Input() inputName = 'listing-search';
  /** Defer loading Places until the user interacts with the field. */
  @Input({ transform: booleanAttribute }) lazyLoad = false;

  @Output() onAddressChange = new EventEmitter<GooglePlaceAddress>();
  @Output() keyupEnter = new EventEmitter<KeyboardEvent>();
  @Output() fallbackValueChange = new EventEmitter<string>();

  @ViewChild('host') host?: ElementRef<HTMLDivElement>;
  @ViewChild('fallbackInput') fallbackInput?: ElementRef<HTMLInputElement>;

  place?: GooglePlaceAddress;
  showFallbackInput = false;
  fallbackValue = '';

  private autocomplete?: google.maps.places.PlaceAutocompleteElement;
  private pendingValue: string | null = null;
  private initPromise?: Promise<void>;
  private activating = false;
  private swapAfterPointerUp?: Promise<void>;
  private pendingPointerFocus?: { clientX: number; clientY: number };

  private selectListener?: (event: google.maps.places.PlacePredictionSelectEvent) => void;
  private keydownListener?: (event: Event) => void;

  constructor(private ngZone: NgZone) {}

  get nativeElement(): HTMLElement {
    return this.autocomplete ?? this.host!.nativeElement;
  }

  ngOnInit(): void {
    // Render the lightweight fallback on the first paint so placeholder text is visible
    // immediately. Previously this waited for ngAfterViewInit, leaving an empty field.
    this.showFallbackInput = this.lazyLoad;
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.lazyLoad) {
      void this.initialize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.autocomplete && (changes['options'] || changes['placeholder'])) {
      this.applyOptions();
    }
  }

  ngOnDestroy(): void {
    this.removeListeners();
    this.autocomplete?.remove();
  }

  getValue(): string {
    return this.autocomplete?.value ?? this.fallbackValue;
  }

  setValue(value: string): void {
    if (this.autocomplete) {
      this.autocomplete.value = value;
      this.pendingValue = null;
      this.fallbackValue = value;
      return;
    }

    this.fallbackValue = value;
    this.pendingValue = value;
  }

  reset(): void {
    this.applyOptions();
  }

  async focusWhenReady(prefill?: string): Promise<void> {
    if (prefill !== undefined && prefill !== '') {
      this.setValue(prefill);
    }

    if (this.lazyLoad && !this.autocomplete) {
      await this.activateLazy();
      return;
    }

    await this.initialize();
    await this.ensureAutocompleteInputFocused();
  }

  onFallbackInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.fallbackValue = value;
    this.pendingValue = value;
    this.fallbackValueChange.emit(value);
  }

  onFallbackPointerDown(event: MouseEvent): void {
    this.pendingPointerFocus = { clientX: event.clientX, clientY: event.clientY };
    this.fallbackInput?.nativeElement.focus();
    this.swapAfterPointerUp ??= this.waitForPointerRelease();
    void this.activateLazy();
  }

  onFallbackFocus(): void {
    if (this.autocomplete || this.activating) {
      return;
    }
    void this.activateLazy();
  }

  onFallbackKeydown(event: KeyboardEvent): void {
    if (event.key?.toLowerCase() !== 'enter') {
      return;
    }

    event.preventDefault();
    this.ngZone.run(() => this.keyupEnter.emit(event));
  }

  private async activateLazy(): Promise<void> {
    if (!this.lazyLoad) {
      this.focusAutocompleteInput();
      return;
    }

    if (this.autocomplete) {
      this.focusAutocompleteInput();
      return;
    }

    if (this.activating) {
      return;
    }

    this.activating = true;
    try {
      if (this.pendingValue === null && this.fallbackValue) {
        this.pendingValue = this.fallbackValue;
      }

      await this.initialize();
      await this.waitForAutocompleteElement();
      await (this.swapAfterPointerUp ?? Promise.resolve());
      this.swapAfterPointerUp = undefined;

      const activeAutocomplete = this.autocomplete as
        | google.maps.places.PlaceAutocompleteElement
        | undefined;
      activeAutocomplete?.classList.remove('google-place-autocomplete-pending');
      this.showFallbackInput = false;
      this.cdr.detectChanges();
      await this.ensureAutocompleteInputFocused();
      this.pendingPointerFocus = undefined;
    } finally {
      this.activating = false;
    }
  }

  private initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.initializeImpl().catch((error) => {
      this.initPromise = undefined;
      throw error;
    });
    return this.initPromise;
  }

  private async initializeImpl(): Promise<void> {
    if (!this.host?.nativeElement || this.autocomplete) {
      return;
    }

    try {
      await loadGoogleMapsPlacesLibrary();

      this.autocomplete = new google.maps.places.PlaceAutocompleteElement(
        toPlaceAutocompleteOptions(this.options),
      );
      this.autocomplete.style.fontFamily = 'Montserrat, sans-serif';
      this.autocomplete.classList.add('google-place-autocomplete-pending');

      this.applyOptions();
      this.host.nativeElement.appendChild(this.autocomplete);
      this.attachListeners();

      if (this.pendingValue !== null) {
        this.autocomplete.value = this.pendingValue;
        this.fallbackValue = this.pendingValue;
        this.pendingValue = null;
      }
    } catch (error) {
      console.error('Google Places autocomplete failed to initialize.', error);
    }
  }

  private applyOptions(): void {
    if (!this.autocomplete) {
      return;
    }

    const mapped = toPlaceAutocompleteOptions(this.options);

    if (mapped.includedRegionCodes) {
      this.autocomplete.includedRegionCodes = mapped.includedRegionCodes;
    }

    if (mapped.locationBias) {
      this.autocomplete.locationBias = mapped.locationBias;
    }

    if (mapped.locationRestriction) {
      this.autocomplete.locationRestriction = mapped.locationRestriction;
    }

    if (mapped.includedPrimaryTypes) {
      this.autocomplete.includedPrimaryTypes = mapped.includedPrimaryTypes;
    }

    this.autocomplete.placeholder = this.placeholder || 'Enter a location';
    this.autocomplete.noInputIcon = true;
    this.autocomplete.noClearButton = true;
    this.autocomplete.setAttribute('no-input-icon', '');
    this.autocomplete.setAttribute('no-clear-button', '');
  }

  private attachListeners(): void {
    if (!this.autocomplete) {
      return;
    }

    this.removeListeners();

    this.selectListener = (event: google.maps.places.PlacePredictionSelectEvent) => {
      void this.handlePlaceSelect(event);
    };

    this.autocomplete.addEventListener('gmp-select', this.selectListener);

    this.keydownListener = (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;

      if (keyboardEvent.key?.toLowerCase() === 'enter') {
        keyboardEvent.preventDefault();
        this.ngZone.run(() => this.keyupEnter.emit(keyboardEvent));
      }
    };

    this.autocomplete.addEventListener('keydown', this.keydownListener);
  }

  private async handlePlaceSelect(
    event: google.maps.places.PlacePredictionSelectEvent,
  ): Promise<void> {
    const place = event.placePrediction.toPlace();

    await place.fetchFields({ fields: ['formattedAddress', 'id', 'displayName', 'viewport'] });

    this.ngZone.run(() => {
      const rawViewport = place.viewport;
      const viewport = rawViewport
        ? {
            north: rawViewport.getNorthEast().lat(),
            south: rawViewport.getSouthWest().lat(),
            east: rawViewport.getNorthEast().lng(),
            west: rawViewport.getSouthWest().lng(),
          }
        : undefined;
      const selectedPlace: GooglePlaceAddress = {
        formatted_address: place.formattedAddress ?? '',
        place_id: place.id,
        name: place.displayName ?? undefined,
        viewport,
      };
      this.place = selectedPlace;

      if (selectedPlace.place_id) {
        this.onAddressChange.emit(selectedPlace);
      }
    });
  }

  private waitForPointerRelease(): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        window.removeEventListener('pointerup', done, true);
        window.removeEventListener('mouseup', done, true);
        resolve();
      };

      window.addEventListener('pointerup', done, true);
      window.addEventListener('mouseup', done, true);
    });
  }

  private async ensureAutocompleteInputFocused(): Promise<void> {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      this.focusAutocompleteInput();

      if (this.isAutocompleteInputFocused()) {
        return;
      }

      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
  }

  private replayPointerAtPendingFocus(): void {
    const pointer = this.pendingPointerFocus;
    if (!pointer) {
      return;
    }

    const target = document.elementFromPoint(pointer.clientX, pointer.clientY);
    if (!target) {
      return;
    }

    const { clientX, clientY } = pointer;
    const pointerInit: PointerEventInit = {
      bubbles: true,
      cancelable: true,
      clientX,
      clientY,
      pointerId: 1,
      pointerType: 'mouse',
      isPrimary: true,
    };
    const mouseInit: MouseEventInit = {
      bubbles: true,
      cancelable: true,
      clientX,
      clientY,
    };

    target.dispatchEvent(new PointerEvent('pointerdown', pointerInit));
    target.dispatchEvent(new MouseEvent('mousedown', mouseInit));
    target.dispatchEvent(new PointerEvent('pointerup', pointerInit));
    target.dispatchEvent(new MouseEvent('mouseup', mouseInit));
    target.dispatchEvent(new MouseEvent('click', mouseInit));
  }

  private isAutocompleteInputFocused(): boolean {
    const element = this.autocomplete;
    if (!element) {
      return false;
    }

    const activeElement = document.activeElement;
    if (!activeElement) {
      return false;
    }

    return activeElement === element || activeElement.closest('gmp-place-autocomplete') === element;
  }

  private async waitForAutocompleteElement(): Promise<void> {
    if (!this.autocomplete) {
      return;
    }

    if (typeof customElements?.whenDefined === 'function') {
      await customElements.whenDefined('gmp-place-autocomplete');
    }

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  private focusAutocompleteInput(): void {
    const element = this.autocomplete;
    if (!element) {
      return;
    }

    this.replayPointerAtPendingFocus();

    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const clientX = rect.left + Math.min(rect.width / 2, rect.width - 8);
      const clientY = rect.top + rect.height / 2;
      const pointerInit: PointerEventInit = {
        bubbles: true,
        cancelable: true,
        clientX,
        clientY,
        pointerId: 1,
        pointerType: 'mouse',
        isPrimary: true,
      };
      const mouseInit: MouseEventInit = {
        bubbles: true,
        cancelable: true,
        clientX,
        clientY,
      };

      element.dispatchEvent(new PointerEvent('pointerdown', pointerInit));
      element.dispatchEvent(new MouseEvent('mousedown', mouseInit));
      element.dispatchEvent(new PointerEvent('pointerup', pointerInit));
      element.dispatchEvent(new MouseEvent('mouseup', mouseInit));
      element.dispatchEvent(new MouseEvent('click', mouseInit));
    }

    if (typeof element.focus === 'function') {
      element.focus();
    }

    const input =
      element.shadowRoot?.querySelector('input')
      ?? element.querySelector('input');
    input?.focus();
  }

  private removeListeners(): void {
    if (!this.autocomplete) {
      return;
    }

    if (this.selectListener) {
      this.autocomplete.removeEventListener('gmp-select', this.selectListener as EventListener);
    }

    if (this.keydownListener) {
      this.autocomplete.removeEventListener('keydown', this.keydownListener);
    }
  }
}
